import React, { useState, useEffect } from "react";
import "./JobSearchPage.css";
import { useToast } from "../components/Toast";
import { HourglassSpinnerWrapper } from "../components/HourglassSpinnerWrapper";
import { MultiSelectChipGroup } from "../components/MultiSelectChipGroup";
import {
  intelligentSearchService,
  IntelligentSearchResult,
} from "../services/intelligentSearchService";
import { IntelligentSearchModal } from "../components/IntelligentSearchModal";
import {
  buildGoogleJobSearchQuery,
  buildJobSearchContext,
  buildTavilyJobSearchQuery,
  createDefaultJobSearchFilters,
  createJobSearchBoards,
  enforceSingleJobSearchBoardSelection,
  getSelectedJobSearchBoards,
  getSelectedJobSearchDomains,
  getSelectedJobSearchSourceIds,
  hydrateJobSearchBoards,
  hydrateJobSearchFilters,
  JOB_SEARCH_JOB_TYPE_OPTIONS,
  JOB_SEARCH_SENIORITY_OPTIONS,
  JOB_SEARCH_TIER_NAMES,
  WEB_JOB_SEARCH_SOURCE_ID,
  type JobSearchBoard,
  type JobSearchFilters,
} from "../features/jobSearch/config";
import {
  createAgent,
  estimateTokens,
  type JobSearchAgentEstimate,
} from "../services/jobSearchAgentService";

interface SearchHistoryItem {
  id: string;
  filters: JobSearchFilters;
  timestamp: string;
  jobBoards: string[];
  query: string;
  intelligentSearch?: boolean;
}

const AGENT_CADENCE_OPTIONS = [
  { value: 1, label: "Every week" },
  { value: 2, label: "Every 2 weeks" },
  { value: 3, label: "Every 3 weeks" },
  { value: 4, label: "Every 4 weeks" },
];

const buildSuggestedAgentName = (filters: JobSearchFilters): string => {
  const keywordLabel = filters.keywords
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
  const locationLabel = filters.location.trim();

  if (keywordLabel && locationLabel) {
    return `${keywordLabel} in ${locationLabel}`;
  }

  if (keywordLabel) {
    return keywordLabel;
  }

  if (locationLabel) {
    return `Jobs in ${locationLabel}`;
  }

  return "Job Search Agent";
};

const JobSearchPage: React.FC = () => {
  const [filters, setFilters] = useState<JobSearchFilters>(
    createDefaultJobSearchFilters,
  );

  const [jobBoards, setJobBoards] = useState<JobSearchBoard[]>(
    createJobSearchBoards,
  );

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [useIntelligentSearch, setUseIntelligentSearch] =
    useState<boolean>(false);
  const [intelligentSearchResults, setIntelligentSearchResults] =
    useState<IntelligentSearchResult | null>(null);
  const [showIntelligentSearchModal, setShowIntelligentSearchModal] =
    useState<boolean>(false);
  const [intelligentSearchLoading, setIntelligentSearchLoading] =
    useState<boolean>(false);
  const [intelligentSearchError, setIntelligentSearchError] = useState<
    string | null
  >(null);
  const [showCreateAgentModal, setShowCreateAgentModal] =
    useState<boolean>(false);
  const [agentName, setAgentName] = useState<string>("");
  const [agentIntervalWeeks, setAgentIntervalWeeks] = useState<number>(1);
  const [agentIsActive, setAgentIsActive] = useState<boolean>(true);
  const [agentSaving, setAgentSaving] = useState<boolean>(false);
  const [agentEstimate, setAgentEstimate] =
    useState<JobSearchAgentEstimate | null>(null);
  const [agentEstimateLoading, setAgentEstimateLoading] =
    useState<boolean>(false);
  const { showToast } = useToast();
  const selectedBoards = getSelectedJobSearchBoards(jobBoards);
  const selectedSourceIds = getSelectedJobSearchSourceIds(jobBoards);
  const selectedSourceNames = selectedBoards.map((board) => board.name);
  const selectedDomains = getSelectedJobSearchDomains(selectedBoards);
  const searchContext = buildJobSearchContext(filters);
  const canSaveAgentFromSearch =
    filters.keywords.trim().length > 0 && selectedSourceIds.length > 0;
  const selectedSourceIdsKey = selectedSourceIds.join("|");

  // Load saved preferences
  useEffect(() => {
    const savedFilters = localStorage.getItem("jobSearchFilters");
    const savedJobBoards = localStorage.getItem("jobSearchBoards");
    const savedHistory = localStorage.getItem("jobSearchHistory");

    if (savedFilters) {
      setFilters(hydrateJobSearchFilters(JSON.parse(savedFilters)));
    }

    if (savedJobBoards) {
      setJobBoards(hydrateJobSearchBoards(JSON.parse(savedJobBoards)));
    }

    if (savedHistory) {
      const parsedHistory = JSON.parse(savedHistory);
      if (Array.isArray(parsedHistory)) {
        setSearchHistory(
          parsedHistory.map((item) => {
            const historyItem =
              item && typeof item === "object"
                ? (item as Partial<SearchHistoryItem> & {
                    filters?: unknown;
                    jobBoards?: unknown;
                  })
                : {};
            return {
              id: String(historyItem.id ?? Date.now()),
              query: String(historyItem.query ?? ""),
              filters: hydrateJobSearchFilters(historyItem.filters),
              timestamp: String(
                historyItem.timestamp ?? new Date().toISOString(),
              ),
              jobBoards: Array.isArray(historyItem.jobBoards)
                ? historyItem.jobBoards.map((board) => String(board))
                : [],
              intelligentSearch: Boolean(historyItem.intelligentSearch),
            };
          }),
        );
      }
    }
  }, []);

  // Save preferences
  useEffect(() => {
    localStorage.setItem("jobSearchFilters", JSON.stringify(filters));
    localStorage.setItem("jobSearchBoards", JSON.stringify(jobBoards));
    localStorage.setItem("jobSearchHistory", JSON.stringify(searchHistory));
  }, [filters, jobBoards, searchHistory]);

  useEffect(() => {
    if (!showCreateAgentModal) {
      setAgentEstimate(null);
      setAgentEstimateLoading(false);
      return;
    }

    if (!canSaveAgentFromSearch) {
      setAgentEstimate(null);
      setAgentEstimateLoading(false);
      return;
    }

    const sourceIds = selectedSourceIdsKey.split("|").filter(Boolean);
    let cancelled = false;
    setAgentEstimateLoading(true);

    void estimateTokens({ sourceIds })
      .then((nextEstimate) => {
        if (!cancelled) {
          setAgentEstimate(nextEstimate);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAgentEstimate(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAgentEstimateLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canSaveAgentFromSearch, selectedSourceIdsKey, showCreateAgentModal]);

  useEffect(() => {
    if (!showCreateAgentModal) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !agentSaving) {
        setShowCreateAgentModal(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [agentSaving, showCreateAgentModal]);

  const handleFilterChange = <K extends keyof JobSearchFilters>(
    field: K,
    value: JobSearchFilters[K],
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMultiSelectFilterChange = (
    field: "jobType" | "seniority",
    value: string[],
  ) => {
    handleFilterChange(field, value);
  };

  const handleJobBoardToggle = (boardId: string) => {
    setJobBoards((prev) => {
      if (useIntelligentSearch) {
        return enforceSingleJobSearchBoardSelection(prev, boardId);
      }

      return prev.map((board) =>
        board.id === boardId ? { ...board, selected: !board.selected } : board,
      );
    });
  };

  const handleSelectAll = () => {
    if (useIntelligentSearch) {
      showToast({
        message:
          "Intelligent Search supports one board at a time. Turn it off to select multiple boards.",
        type: "info",
      });
      return;
    }

    setJobBoards((prev) => prev.map((board) => ({ ...board, selected: true })));
  };

  const handleSelectNone = () => {
    if (useIntelligentSearch) {
      showToast({
        message:
          "Intelligent Search requires one board selected. Turn it off to clear selections.",
        type: "info",
      });
      return;
    }

    setJobBoards((prev) =>
      prev.map((board) => ({ ...board, selected: false })),
    );
  };

  const handleSearch = async () => {
    if (!filters.keywords.trim()) {
      showToast({
        message: "Please enter keywords to search for",
        type: "warning",
      });
      return;
    }

    if (selectedBoards.length === 0 && !useIntelligentSearch) {
      showToast({
        message: "Please select at least one job board",
        type: "warning",
      });
      return;
    }

    if (useIntelligentSearch && selectedBoards.length !== 1) {
      showToast({
        message:
          "Intelligent Search works with one job board at a time. Please select a single board.",
        type: "warning",
      });
      return;
    }

    setIsSearching(true);
    setIntelligentSearchLoading(useIntelligentSearch);
    setIntelligentSearchError(null);

    try {
      if (useIntelligentSearch) {
        const results = await intelligentSearchService.search({
          query: buildTavilyJobSearchQuery(filters),
          sources: selectedSourceIds.includes(WEB_JOB_SEARCH_SOURCE_ID)
            ? undefined
            : selectedDomains.length > 0
              ? selectedDomains
              : undefined,
          searchType: "job",
          recencyDays: filters.recencyDays ?? undefined,
        });

        setIntelligentSearchResults(results);
        setShowIntelligentSearchModal(true);
        setIntelligentSearchLoading(false);

        // Save search to history
        const newSearch = {
          id: Date.now().toString(),
          query: filters.keywords,
          filters: { ...filters },
          jobBoards: selectedBoards.map((b) => b.name),
          timestamp: new Date().toISOString(),
          intelligentSearch: true,
        };

        const updatedHistory = [
          newSearch,
          ...searchHistory.filter((h) => h.id !== newSearch.id),
        ];

        // Keep only last 50 searches
        if (updatedHistory.length > 50) {
          updatedHistory.splice(50);
        }

        setSearchHistory(updatedHistory);
      } else {
        const googleQuery = buildGoogleJobSearchQuery(filters, selectedDomains);

        // Generate Google search URL
        const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(googleQuery)}`;

        // Open in new tab
        window.open(googleUrl, "_blank");

        // Save search to history
        const newSearch = {
          id: Date.now().toString(),
          query: googleQuery,
          filters: { ...filters },
          jobBoards: selectedBoards.map((b) => b.name),
          timestamp: new Date().toISOString(),
        };

        const updatedHistory = [
          newSearch,
          ...searchHistory.filter((h) => h.id !== newSearch.id),
        ];

        // Keep only last 50 searches
        if (updatedHistory.length > 50) {
          updatedHistory.splice(50);
        }

        setSearchHistory(updatedHistory);
      }
    } catch (error: any) {
      if (useIntelligentSearch) {
        setIntelligentSearchError(
          error.message ||
            "Error performing intelligent search. Please try again.",
        );
        setIntelligentSearchLoading(false);
        showToast({
          message:
            error.message ||
            "Error performing intelligent search. Please try again.",
          type: "error",
        });
      } else {
        showToast({
          message: "Error performing search. Please try again.",
          type: "error",
        });
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleOpenCreateAgentModal = () => {
    if (!filters.keywords.trim()) {
      showToast({
        message: "Add keywords before saving this search as an agent",
        type: "warning",
      });
      return;
    }

    if (selectedSourceIds.length === 0) {
      showToast({
        message: "Select at least one source before creating an agent",
        type: "warning",
      });
      return;
    }

    setAgentName(buildSuggestedAgentName(filters));
    setAgentIntervalWeeks(1);
    setAgentIsActive(true);
    setShowCreateAgentModal(true);
  };

  const handleSaveAgentFromSearch = async () => {
    if (!agentName.trim()) {
      showToast({
        message: "Add an agent name before saving",
        type: "warning",
      });
      return;
    }

    if (!canSaveAgentFromSearch) {
      showToast({
        message:
          "Add keywords and at least one source before saving this search as an agent",
        type: "warning",
      });
      return;
    }

    setAgentSaving(true);

    try {
      const savedAgent = await createAgent({
        name: agentName.trim(),
        filters,
        sourceIds: selectedSourceIds,
        intervalWeeks: agentIntervalWeeks,
        isActive: agentIsActive,
      });

      setShowCreateAgentModal(false);
      setAgentEstimate(null);
      showToast({
        message: `Saved "${savedAgent.name}" as an agent`,
        description:
          "Manage runs, history, and schedule changes from the Job Search Agents page.",
        type: "success",
      });
    } catch (error: any) {
      showToast({
        message: error?.message || "Failed to save this search as an agent",
        type: "error",
      });
    } finally {
      setAgentSaving(false);
    }
  };

  const handleLoadFromHistory = (historyItem: SearchHistoryItem) => {
    setFilters(historyItem.filters);
    // Update job boards selection based on history
    setJobBoards((prev) => {
      if (!useIntelligentSearch) {
        return prev.map((board) => ({
          ...board,
          selected: historyItem.jobBoards.includes(board.name),
        }));
      }

      const fallbackBoardName = historyItem.jobBoards[0] ?? prev[0]?.name;

      return prev.map((board) => ({
        ...board,
        selected: board.name === fallbackBoardName,
      }));
    });
    setShowHistory(false);
  };

  const handleDeleteFromHistory = (historyId: string) => {
    setSearchHistory((prev) => prev.filter((item) => item.id !== historyId));
  };

  const handleClearHistory = () => {
    setSearchHistory([]);
    setShowHistory(false);
  };

  // Show large centered spinner when intelligent search is loading
  if (intelligentSearchLoading) {
    return (
      <div className="job-search-container">
        <div className="job-search-loading-wrapper">
          <HourglassSpinnerWrapper
            fullPage={false}
            title="Searching jobs..."
            subtitle="Preparing your intelligent search across selected platforms"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="job-search-container">
      <div className="job-search-content">
        <div className="job-search-header" data-tour-id="job-search-header">
          <h1>🔍 Job Search Intelligence</h1>
          <div className="job-search-header-actions">
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setShowHistory(!showHistory)}
              data-tour-id="job-search-history"
            >
              📚 History ({searchHistory.length})
            </button>
          </div>
        </div>

        <div className="job-search-grid">
          {/* Search Form */}
          <div className="job-search-inputs">
            <div className="job-search-form">
              {/* Primary Search Fields */}
              <div className="form-group" data-tour-id="search-input">
                <label className="form-label">Keywords *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., software engineer, data scientist"
                  value={filters.keywords}
                  onChange={(e) =>
                    handleFilterChange("keywords", e.target.value)
                  }
                />
                <div className="form-help-text">
                  Job titles, skills, or technologies
                </div>
              </div>

              <div className="form-row form-row--filters">
                <div className="form-group" data-tour-id="search-location">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., San Francisco, Remote"
                    value={filters.location}
                    onChange={(e) =>
                      handleFilterChange("location", e.target.value)
                    }
                  />
                  <div className="form-help-text">City, state, or "Remote"</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Recency</label>
                  <select
                    className="form-input"
                    value={String(filters.recencyDays ?? 7)}
                    onChange={(e) =>
                      handleFilterChange("recencyDays", Number(e.target.value))
                    }
                  >
                    <option value="1">Past 24 hours</option>
                    <option value="7">Past week</option>
                    <option value="30">Past month</option>
                    <option value="90">Past 3 months</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Experience</label>
                  <select
                    className="form-input"
                    value={filters.experience}
                    onChange={(e) =>
                      handleFilterChange("experience", e.target.value)
                    }
                    data-tour-id="search-experience"
                  >
                    <option value="">Any experience</option>
                    <option value="0-1 years OR entry">0-1 years</option>
                    <option value="1-3 years OR 2-4 years">1-3 years</option>
                    <option value="3-5 years OR 4-6 years">3-5 years</option>
                    <option value="5+ years OR 6+ years">5+ years</option>
                    <option value="7+ years OR 8+ years">7+ years</option>
                  </select>
                </div>
              </div>

              <div className="form-group" data-tour-id="search-filters">
                <label className="form-label">Job Type</label>
                <MultiSelectChipGroup
                  ariaLabel="Job Type"
                  options={JOB_SEARCH_JOB_TYPE_OPTIONS}
                  selectedValues={filters.jobType}
                  onChange={(value) =>
                    handleMultiSelectFilterChange("jobType", value)
                  }
                />
                <div className="form-help-text">
                  Select one or more job types. Leave blank for any type.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Seniority</label>
                <MultiSelectChipGroup
                  ariaLabel="Seniority"
                  options={JOB_SEARCH_SENIORITY_OPTIONS}
                  selectedValues={filters.seniority}
                  onChange={(value) =>
                    handleMultiSelectFilterChange("seniority", value)
                  }
                  dataTourId="search-seniority"
                />
                <div className="form-help-text">
                  Select one or more seniority bands. Leave blank for any level.
                </div>
              </div>

              {/* Optional Fields */}
              <div className="form-group">
                <label className="form-label">Exclude Keywords</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., senior, manager, director"
                  value={filters.exclusions}
                  onChange={(e) =>
                    handleFilterChange("exclusions", e.target.value)
                  }
                  data-tour-id="search-exclude"
                />
                <div className="form-help-text">
                  Keywords to exclude from results
                </div>
              </div>

              {/* Intelligent Search Toggle */}
              <div
                className="form-group"
                data-tour-id="intelligent-search-toggle"
              >
                <label className="form-label intelligent-search-label">
                  <input
                    type="checkbox"
                    className="intelligent-search-checkbox"
                    checked={useIntelligentSearch}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setUseIntelligentSearch(enabled);
                      if (enabled) {
                        setJobBoards((prev) =>
                          enforceSingleJobSearchBoardSelection(prev),
                        );
                      }
                    }}
                  />
                  Use Intelligent Search
                  <span className="intelligent-search-badge">(AI-Powered)</span>
                </label>
                <div className="form-help-text">
                  AI-powered search with latest information, intelligent
                  filtering, and deduplication. Select one source when enabled.
                </div>
              </div>
            </div>

            <div className="job-search-actions" data-tour-id="search-button">
              <div className="job-search-actions__row">
                <button
                  className={`btn btn-primary job-search-action ${isSearching ? "btn-disabled" : ""}`}
                  onClick={handleSearch}
                  disabled={isSearching || !filters.keywords.trim()}
                >
                  {isSearching
                    ? "🔍 Searching..."
                    : useIntelligentSearch
                      ? "🧠 Intelligent Search"
                      : "🚀 Search Jobs"}
                </button>
                <button
                  className={`btn job-search-action job-search-action--secondary ${
                    !canSaveAgentFromSearch || agentSaving ? "btn-disabled" : ""
                  }`}
                  onClick={handleOpenCreateAgentModal}
                  disabled={!canSaveAgentFromSearch || agentSaving}
                  title={
                    canSaveAgentFromSearch
                      ? "Save this search configuration as a scheduled agent"
                      : "Add keywords and keep at least one source selected to save an agent"
                  }
                >
                  {agentSaving ? "Saving..." : "🤖 Save as Agent"}
                </button>
              </div>
              <div className="job-search-actions__hint">
                Search once now, or save the same configuration as a scheduled
                agent.
              </div>
            </div>
          </div>

          {/* Job Boards Selection */}
          <div
            className="job-boards-section"
            data-tour-id="job-boards-selection"
          >
            <div className="job-boards-header">
              <h2>Search Sources</h2>
              <span className="job-boards-subtitle">
                {useIntelligentSearch
                  ? "Select one source for Intelligent Search"
                  : "Select sources to search across"}
              </span>
            </div>

            <div className="job-boards-grid">
              {[0, 1, 2, 3, 5].map((tier) => {
                const tierBoards = jobBoards.filter(
                  (board) => board.tier === tier,
                );
                if (tierBoards.length === 0) return null;

                return (
                  <div key={tier} className="job-board-tier">
                    <div className="job-board-tier-title">
                      {JOB_SEARCH_TIER_NAMES[tier]}
                    </div>
                    <div className="job-board-tier-grid">
                      {tierBoards.map((board) => (
                        <div
                          key={board.id}
                          className={`job-board-item ${board.selected ? "selected" : ""}`}
                          onClick={() => handleJobBoardToggle(board.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleJobBoardToggle(board.id);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <input
                            type="checkbox"
                            className="job-board-checkbox"
                            checked={board.selected}
                            onChange={() => handleJobBoardToggle(board.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="job-board-icon">{board.icon}</span>
                          <div className="job-board-info">
                            <div className="job-board-label">{board.name}</div>
                            <div className="job-board-description">
                              {board.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="job-boards-controls">
              <button
                className={`btn btn-outline ${useIntelligentSearch ? "btn-disabled" : ""}`}
                onClick={handleSelectAll}
                data-tour-id="job-boards-select-all"
                aria-disabled={useIntelligentSearch}
                title={
                  useIntelligentSearch
                    ? "Turn off Intelligent Search to select all boards."
                    : undefined
                }
              >
                Select All
              </button>
              <button
                className={`btn btn-outline ${useIntelligentSearch ? "btn-disabled" : ""}`}
                onClick={handleSelectNone}
                data-tour-id="job-boards-select-none"
                aria-disabled={useIntelligentSearch}
                title={
                  useIntelligentSearch
                    ? "Turn off Intelligent Search to clear selections."
                    : undefined
                }
              >
                Select None
              </button>
            </div>
          </div>
        </div>

        {/* Intelligent Search Modal */}
        <IntelligentSearchModal
          isOpen={showIntelligentSearchModal}
          onClose={() => {
            setShowIntelligentSearchModal(false);
            setIntelligentSearchResults(null);
            setIntelligentSearchError(null);
          }}
          results={intelligentSearchResults}
          isLoading={intelligentSearchLoading}
          error={intelligentSearchError}
        />

        {showCreateAgentModal && (
          <div
            className="job-search-agent-modal"
            onClick={() => {
              if (!agentSaving) {
                setShowCreateAgentModal(false);
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="job-search-agent-modal-title"
          >
            <div
              className="job-search-agent-modal__content"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <div className="job-search-agent-modal__header">
                <div>
                  <h3 id="job-search-agent-modal-title">
                    Save Search as Agent
                  </h3>
                  <p>
                    Reuse your current filters and selected sources, then set
                    the schedule details here.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowCreateAgentModal(false)}
                  disabled={agentSaving}
                >
                  Close
                </button>
              </div>

              <div className="job-search-agent-modal__body">
                <div className="job-search-agent-summary">
                  <div className="job-search-agent-summary__item">
                    <span>Keywords</span>
                    <strong>{filters.keywords.trim()}</strong>
                  </div>
                  <div className="job-search-agent-summary__item">
                    <span>Sources</span>
                    <strong>{selectedSourceNames.join(", ")}</strong>
                  </div>
                  <div className="job-search-agent-summary__item">
                    <span>Context</span>
                    <strong>{searchContext || "No extra filters"}</strong>
                  </div>
                  <div className="job-search-agent-summary__item">
                    <span>Run Cost</span>
                    <strong>
                      {agentEstimateLoading
                        ? "Calculating..."
                        : agentEstimate
                          ? `${agentEstimate.tokensPerRun} tokens per run across ${agentEstimate.sourceCount} sources`
                          : "Estimate unavailable"}
                    </strong>
                  </div>
                </div>

                <div className="job-search-agent-modal__form">
                  <label className="form-group" htmlFor="job-search-agent-name">
                    <span className="form-label">Agent Name</span>
                    <input
                      id="job-search-agent-name"
                      type="text"
                      className="form-input"
                      value={agentName}
                      onChange={(event) => setAgentName(event.target.value)}
                      placeholder="Remote product roles"
                    />
                  </label>

                  <div className="job-search-agent-modal__form-row">
                    <label
                      className="form-group"
                      htmlFor="job-search-agent-cadence"
                    >
                      <span className="form-label">Cadence</span>
                      <select
                        id="job-search-agent-cadence"
                        className="form-input"
                        value={String(agentIntervalWeeks)}
                        onChange={(event) =>
                          setAgentIntervalWeeks(Number(event.target.value))
                        }
                      >
                        {AGENT_CADENCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label
                      className="job-search-agent-toggle"
                      htmlFor="job-search-agent-active"
                    >
                      <input
                        id="job-search-agent-active"
                        type="checkbox"
                        checked={agentIsActive}
                        onChange={(event) =>
                          setAgentIsActive(event.target.checked)
                        }
                      />
                      <span>Agent starts active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="job-search-agent-modal__footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowCreateAgentModal(false)}
                  disabled={agentSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-primary job-search-agent-modal__save ${agentSaving ? "btn-disabled" : ""}`}
                  onClick={handleSaveAgentFromSearch}
                  disabled={agentSaving}
                >
                  {agentSaving ? "Saving Agent..." : "Save Agent"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search History Popup */}
        {showHistory && (
          <div
            className="search-history-panel"
            onClick={() => setShowHistory(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setShowHistory(false);
              }
            }}
            role="dialog"
            tabIndex={0}
          >
            <div
              className="search-history-content"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="search-history-header">
                <h3>📚 Search History</h3>
                <div className="search-history-actions">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleClearHistory}
                  >
                    Clear All
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setShowHistory(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="search-history-list">
                {searchHistory.length === 0 ? (
                  <div className="search-history-empty">
                    <p>
                      No search history yet. Start searching to build your
                      history!
                    </p>
                  </div>
                ) : (
                  searchHistory.map((item) => (
                    <div key={item.id} className="search-history-item">
                      <div className="search-history-item-content">
                        <div className="search-history-query">{item.query}</div>
                        <div className="search-history-details">
                          <span className="search-history-boards">
                            {item.jobBoards.join(", ")}
                          </span>
                          <span className="search-history-time">
                            {new Date(item.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="search-history-item-actions">
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleLoadFromHistory(item)}
                        >
                          Load
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleDeleteFromHistory(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobSearchPage;
